import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Receipt, Wallet, ChevronRight, AlertCircle, ChevronDown, Gift } from 'lucide-react';
import { createProductSale, CreateProductSaleData, CounterCheckoutPayment } from '../lib/productSales';
import { supabase } from '../lib/supabaseClient';
import { StockItem } from '../lib/stockItems';
import { Customer } from '../lib/customers';
import ProductSaleSuccessModal from './ProductSaleSuccessModal';
import { useAppSystem } from '../hooks/useAppSystem';
import { GiftCardService } from '../services/giftCardService';
import StripeCardEntry from './StripeCardEntry';

interface CartItem {
  product: StockItem;
  quantity: number;
}

interface RecordProductSalePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId: string;
  cart: CartItem[];
  total: number;
  selectedCustomer?: Customer | null;
  appliedDiscount?: {
    id?: string;
    code?: string;
    type: 'percentage' | 'fixed';
    value: number;
    description?: string;
    discountAmount: number;
    source: 'code' | 'custom';
  } | null;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  /** Combined tax rate the quote used (display only). */
  taxRatePercent?: number;
  /**
   * Point-of-sale checkout: when provided, every payment path creates the
   * sale through this (the shared fulfillment engine — deposits, containers,
   * wallet, rentals) instead of the bare product-sale RPC. `payment` is null
   * when the money was already taken (Stripe card entry) and is linked after.
   */
  createSale?: (payment: CounterCheckoutPayment | null) => Promise<{
    id: string;
    sale_number: string;
    invoice_id: string | null;
  }>;
  /** Preselect "save card on file" for Stripe payments (e.g. cart has a rental). */
  suggestSaveCard?: boolean;
}

const paymentMethods = [
  {
    id: 'credit_card',
    name: 'Record Credit / Debit',
    icon: <CreditCard className="h-5 w-5 text-purple-500" />,
    description: 'Record credit or debit card payment'
  },
  {
    id: 'bank_transfer',
    name: 'Record Bank Transfer',
    icon: <Receipt className="h-5 w-5 text-green-500" />,
    description: 'Record direct bank transfer payment'
  },
  {
    id: 'cash',
    name: 'Record Cash',
    icon: <Wallet className="h-5 w-5 text-yellow-500" />,
    description: 'Record cash payment'
  }
];

export default function RecordProductSalePaymentModal({
  isOpen,
  onClose,
  onSuccess,
  businessId,
  cart,
  total,
  selectedCustomer: propSelectedCustomer,
  appliedDiscount,
  subtotal,
  discountAmount,
  taxAmount,
  taxRatePercent,
  createSale,
  suggestSaveCard = false,
}: RecordProductSalePaymentModalProps) {
  const { coreApps, installedAddons } = useAppSystem();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState(paymentMethods[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [paymentType, setPaymentType] = useState<'manual' | 'stripe' | 'gift_card' | null>(null);
  const [stripeConnectEnabled, setStripeConnectEnabled] = useState(false);
  // Use the customer passed from the parent cart
  const selectedCustomer = propSelectedCustomer;
  
  // Gift card state
  const [giftCardAppInstalled, setGiftCardAppInstalled] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [validatedGiftCard, setValidatedGiftCard] = useState<any>(null);
  const [giftCardBalance, setGiftCardBalance] = useState<number | null>(null);
  const [giftCardRedemptionAmount, setGiftCardRedemptionAmount] = useState('');
  const [validatingGiftCard, setValidatingGiftCard] = useState(false);
  const [giftCardError, setGiftCardError] = useState<string | null>(null);
  const [saveNewCardOnFile, setSaveNewCardOnFile] = useState(suggestSaveCard);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [completedSale, setCompletedSale] = useState<{
    saleNumber: string;
    productSaleId?: string;
    saleData: {
      subtotal: number;
      taxAmount: number;
      total: number;
      items: Array<{
        name: string;
        quantity: number;
        price: number;
      }>;
    };
  } | null>(null);

  // Totals come from the server quote; per-line figures are display only.
  const taxCalculation = React.useMemo(() => {
    const rate = taxRatePercent ?? 0;
    return {
      subtotal: subtotal,
      tax_rate: rate,
      tax_amount: taxAmount,
      total: total,
      items: cart.map(item => ({
        price: item.product.price,
        quantity: item.quantity,
        tax_exempt: false,
        tax_rate: rate,
        tax_amount: (item.product.price * item.quantity * rate) / 100,
        total_price: item.product.price * item.quantity * (1 + rate / 100)
      }))
    };
  }, [cart, subtotal, taxAmount, total, taxRatePercent]);

  // Route sale creation through the parent's checkout when it supplies one.
  const runSale = async (
    payment: CounterCheckoutPayment | null,
    legacy: () => CreateProductSaleData,
  ): Promise<{ id: string; sale_number: string; invoice_id: string | null }> => {
    if (createSale) return createSale(payment);
    const sale = await createProductSale(legacy());
    return { id: sale.id, sale_number: sale.sale_number, invoice_id: sale.invoice_id ?? null };
  };

  useEffect(() => {
    console.log('💳 RecordProductSalePaymentModal props changed:', {
      isOpen,
      businessId,
      cartLength: cart.length,
      total,
      taxCalculation
    });
    if (isOpen) {
      console.log('💳 Payment modal opening...');
      setIsAnimating(true);
      
      // Reset form state
      setSelectedMethod(paymentMethods[0]);
      setPaymentType(null);
      setIsDropdownOpen(false);
      setError(null);
      
      // Check Stripe Connect status
      checkStripeConnectStatus();
      
      // Reset gift card state
      setGiftCardCode('');
      setValidatedGiftCard(null);
      setGiftCardBalance(null);
      setGiftCardRedemptionAmount('');
      setGiftCardError(null);
      setSaveNewCardOnFile(false);
      
      // Check if gift card app is installed
      checkGiftCardAppInstalled();
    }
  }, [isOpen, businessId, cart.length, total, taxCalculation]);

  const checkGiftCardAppInstalled = () => {
    const allApps = [...coreApps, ...installedAddons];
    const hasGiftCardApp = allApps.some(
      app => app.app?.slug === 'gift-cards' && app.status === 'active'
    );
    setGiftCardAppInstalled(hasGiftCardApp);
  };

  const checkStripeConnectStatus = async () => {
    try {
      const { data: business, error } = await supabase
        .from('businesses')
        .select('stripe_connect_account_id, stripe_connect_charges_enabled')
        .eq('id', businessId)
        .single();

      if (!error && business) {
        const isEnabled = business.stripe_connect_account_id && business.stripe_connect_charges_enabled;
        setStripeConnectEnabled(!!isEnabled);
      }
    } catch (err) {
      console.error('Error checking Stripe Connect status:', err);
      setStripeConnectEnabled(false);
    }
  };

  useEffect(() => {
    checkGiftCardAppInstalled();
  }, [coreApps, installedAddons]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setSelectedMethod(paymentMethods[0]);
      setPaymentType(null);
      setIsDropdownOpen(false);
      setShowSuccessModal(false);
      setCompletedSale(null);
      
      // Reset gift card state
      setGiftCardCode('');
      setValidatedGiftCard(null);
      setGiftCardBalance(null);
      setGiftCardRedemptionAmount('');
      setGiftCardError(null);
    }
  };

  const handleValidateGiftCard = async () => {
    if (!giftCardCode.trim()) {
      setGiftCardError('Please enter a gift card code');
      return;
    }

    try {
      setValidatingGiftCard(true);
      setGiftCardError(null);

      const validation = await GiftCardService.validateForRedemption(
        businessId,
        giftCardCode.trim().toUpperCase()
      );

      if (!validation.isValid || !validation.giftCard) {
        setGiftCardError(validation.error || 'Invalid gift card');
        setValidatedGiftCard(null);
        setGiftCardBalance(null);
        return;
      }

      setValidatedGiftCard(validation.giftCard);
      setGiftCardBalance(validation.giftCard.current_balance);
      
      // Set max redemption amount
      const maxAmount = Math.min(validation.giftCard.current_balance, taxCalculation.total);
      setGiftCardRedemptionAmount(maxAmount.toFixed(2));
    } catch (error) {
      console.error('Error validating gift card:', error);
      setGiftCardError('Failed to validate gift card. Please try again.');
      setValidatedGiftCard(null);
      setGiftCardBalance(null);
    } finally {
      setValidatingGiftCard(false);
    }
  };

  const handleGiftCardRedemption = async () => {
    if (!validatedGiftCard) {
      setError('Gift card must be validated');
      return;
    }

    const redemptionAmount = parseFloat(giftCardRedemptionAmount);
    if (isNaN(redemptionAmount) || redemptionAmount <= 0) {
      setError('Please enter a valid redemption amount');
      return;
    }

    if (redemptionAmount > taxCalculation.total) {
      setError(`Redemption amount cannot exceed total amount ($${taxCalculation.total.toFixed(2)})`);
      return;
    }

    if (redemptionAmount > (giftCardBalance || 0)) {
      setError(`Redemption amount cannot exceed gift card balance ($${giftCardBalance?.toFixed(2)})`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create the product sale first
      const sale = await runSale(
        {
          method: 'other',
          amount: redemptionAmount,
          reference: `Gift Card: ${validatedGiftCard.code}`,
          gift_card_id: validatedGiftCard.id,
        },
        () => ({
        business_id: businessId,
        customer_id: selectedCustomer?.id,
        subtotal_amount: taxCalculation.subtotal,
        tax_rate: taxCalculation.tax_rate,
        tax_amount: taxCalculation.tax_amount,
        total_amount: taxCalculation.total,
        discount_amount: discountAmount > 0 ? discountAmount : undefined,
        discount_details: appliedDiscount ? [{
          id: appliedDiscount.id || 'custom',
          title: appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : (appliedDiscount.description || 'Custom Discount'),
          type: appliedDiscount.type,
          amount: appliedDiscount.value
        }] : undefined,
        original_subtotal: discountAmount > 0 ? subtotal + discountAmount : undefined,
        payment_status: 'paid',
        payment_method: 'other',
        payment_date: new Date().toISOString(),
        payment_amount: redemptionAmount,
        payment_reference: `Gift Card: ${validatedGiftCard.code}`,
        gift_card_id: validatedGiftCard.id,
        expected_total_cents: Math.round(total * 100),
        notes: `Gift card redemption - ${validatedGiftCard.code}`,
        items: cart.map((item, index) => {
          const taxItem = taxCalculation.items[index];
          return {
            stock_item_id: item.product.id,
            product_name: item.product.name,
            quantity: item.quantity,
            unit_price: item.product.price,
            tax_exempt: taxItem.tax_exempt,
            tax_rate: taxItem.tax_rate,
            tax_amount: taxItem.tax_amount,
            total_price: taxItem.total_price
          };
        })
      }));

      // Manually redeem the gift card for product sale
      // Update gift card balance
      const balanceBefore = validatedGiftCard.current_balance;
      const balanceAfter = balanceBefore - redemptionAmount;

      const { error: updateError } = await supabase
        .from('gift_cards')
        .update({
          current_balance: balanceAfter,
          updated_at: new Date().toISOString()
        })
        .eq('id', validatedGiftCard.id);

      if (updateError) {
        throw new Error('Failed to update gift card balance');
      }

      // Create gift card transaction (for product sales, we'll use notes to indicate it's a product sale)
      const { error: transactionError } = await supabase
        .from('gift_card_transactions')
        .insert({
          gift_card_id: validatedGiftCard.id,
          transaction_type: 'redemption',
          amount: redemptionAmount,
          balance_before: balanceBefore,
          balance_after: balanceAfter,
          notes: `Product Sale #${sale.sale_number}`
        });

      if (transactionError) {
        console.error('Failed to create gift card transaction:', transactionError);
        // Don't fail the whole process if transaction logging fails
      }

      // The payment itself was recorded by the sale RPC (with the gift card
      // linked), so no separate payments insert is needed here.

      // Prepare success modal data
      setCompletedSale({
        saleNumber: sale.sale_number,
        productSaleId: sale.id,
        saleData: {
          subtotal: taxCalculation.subtotal,
          taxAmount: taxCalculation.tax_amount,
          total: taxCalculation.total,
          discountAmount: discountAmount > 0 ? discountAmount : undefined,
          discountDetails: appliedDiscount ? {
            source: appliedDiscount.source,
            code: appliedDiscount.code,
            description: appliedDiscount.description,
            type: appliedDiscount.type,
            value: appliedDiscount.value
          } : undefined,
          items: cart.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            price: item.product.price
          }))
        }
      });

      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error processing gift card redemption:', error);
      setError(error instanceof Error ? error.message : 'Failed to process gift card redemption');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For gift card payments, handleGiftCardRedemption handles the submission
    if (paymentType === 'gift_card') {
      return;
    }

    if (!businessId || cart.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      console.log('🎯 Creating product sale with payment');

      // Create the sale — the server atomically moves stock, issues the
      // invoice, and records the payment (no separate payments insert here).
      const sale = await runSale(
        { method: selectedMethod.id, amount: total },
        () => ({
        business_id: businessId,
        customer_id: selectedCustomer?.id, // Add customer if selected
        subtotal_amount: taxCalculation.subtotal,
        tax_rate: taxCalculation.tax_rate,
        tax_amount: taxCalculation.tax_amount,
        total_amount: taxCalculation.total,
        discount_amount: discountAmount > 0 ? discountAmount : undefined,
        discount_details: appliedDiscount ? [{
          id: appliedDiscount.id || 'custom',
          title: appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : (appliedDiscount.description || 'Custom Discount'),
          type: appliedDiscount.type,
          amount: appliedDiscount.value
        }] : undefined,
        original_subtotal: discountAmount > 0 ? subtotal + discountAmount : undefined,
        payment_status: 'paid', // Mark as paid since we're processing payment
        payment_method: selectedMethod.id,
        payment_date: new Date().toISOString(),
        expected_total_cents: Math.round(total * 100),
        notes: `Payment via ${selectedMethod.name}${selectedCustomer ? ` - Customer: ${selectedCustomer.first_name} ${selectedCustomer.last_name}` : ''}${appliedDiscount ? ` - Discount: ${appliedDiscount.source === 'code' ? appliedDiscount.code : appliedDiscount.description}` : ''}`,
        items: cart.map((item, index) => {
          const taxItem = taxCalculation.items[index];
          return {
            stock_item_id: item.product.id,
            product_name: item.product.name,
            quantity: item.quantity,
            unit_price: item.product.price,
            tax_exempt: taxItem.tax_exempt,
            tax_rate: taxItem.tax_rate,
            tax_amount: taxItem.tax_amount,
            total_price: taxItem.total_price
          };
        })
      }));

      console.log('✅ Product sale created successfully:', sale);

      // Prepare success modal data
      setCompletedSale({
        saleNumber: sale.sale_number,
        productSaleId: sale.id,
        saleData: {
          subtotal: taxCalculation.subtotal,
          taxAmount: taxCalculation.tax_amount,
          total: taxCalculation.total,
          discountAmount: discountAmount > 0 ? discountAmount : undefined,
          discountDetails: appliedDiscount ? {
            source: appliedDiscount.source,
            code: appliedDiscount.code,
            description: appliedDiscount.description,
            type: appliedDiscount.type,
            value: appliedDiscount.value
          } : undefined,
          items: cart.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            price: item.product.price
          }))
        }
      });

      // Show success modal
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('❌ Failed to create product sale:', error);
      
      // Check if it's a stock quantity error
      if (error.message && error.message.includes('Insufficient stock quantity')) {
        setError('Insufficient stock: One or more products do not have enough inventory available.');
      } else {
        setError(error instanceof Error ? error.message : 'Failed to process payment');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCompletedSale(null);
    onSuccess();
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      } z-[100030]`}
      style={{
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Payment
            </h2>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Payment Option Buttons */}
              <button
                type="button"
                onClick={() => {
                  setPaymentType('manual');
                  setSelectedMethod(paymentMethods[0]);
                }}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl font-medium text-base transition-all duration-200 ${
                  paymentType === 'manual'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-center">
                  <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                    paymentType === 'manual'
                      ? 'border-white'
                      : 'border-gray-400'
                  }`}>
                    {paymentType === 'manual' && (
                      <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                    )}
                  </div>
                  <span>Record Transaction</span>
                </div>
                <span className="font-semibold">${taxCalculation.total.toFixed(2)}</span>
              </button>

              {/* Additional Payment Methods */}
              {stripeConnectEnabled && (
                <button
                  type="button"
                  onClick={() => {
                    setPaymentType('stripe');
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl font-medium text-base transition-all duration-200 ${
                    paymentType === 'stripe'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      paymentType === 'stripe'
                        ? 'border-white'
                        : 'border-gray-400'
                    }`}>
                      {paymentType === 'stripe' && (
                        <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                      )}
                    </div>
                    <span>Stripe Pay</span>
                  </div>
                  <span className="font-semibold">${taxCalculation.total.toFixed(2)}</span>
                </button>
              )}

              {giftCardAppInstalled && (
                <button
                  type="button"
                  onClick={() => {
                    setPaymentType('gift_card');
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl font-medium text-base transition-all duration-200 ${
                    paymentType === 'gift_card'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      paymentType === 'gift_card'
                        ? 'border-white'
                        : 'border-gray-400'
                    }`}>
                      {paymentType === 'gift_card' && (
                        <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                      )}
                    </div>
                    <span>Gift Card</span>
                  </div>
                </button>
              )}

              {/* Payment Details - Clean Style */}
              {paymentType === 'gift_card' ? (
                <div className="space-y-4">
                  {/* Empty space - gift card info and redemption amount will be in footer */}
                </div>
              ) : paymentType === 'stripe' ? (
                <div className="space-y-4">
                  {/* Empty space - card entry form will be in footer */}
                </div>
              ) : paymentType === 'manual' ? (
                <div className="space-y-4">
                  {/* Empty space - payment method will be in footer */}
                </div>
              ) : null}
            </div>
          </form>

          {/* Fixed Footer with Pay Button */}
          {paymentType && (
          <div className="border-t border-gray-200 bg-white" style={{
            paddingTop: isMobile ? '12px' : '16px',
            paddingBottom: isMobile ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : '16px',
            paddingLeft: isMobile ? '16px' : '24px',
            paddingRight: isMobile ? '16px' : '24px'
          }}>
            {/* Payment Method Selection - Only for manual payments */}
            {paymentType === 'manual' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-center">
                      {selectedMethod.icon}
                      <span className="ml-3 text-base font-medium text-gray-900">{selectedMethod.name}</span>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`} />
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden">
                      {paymentMethods.map((method) => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => {
                            setSelectedMethod(method);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full flex items-center px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                            selectedMethod.id === method.id ? 'bg-blue-50' : ''
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {method.icon}
                          <span className="ml-3 text-sm font-medium text-gray-900">{method.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Gift Card Code Input - Only for gift card payments, before validation */}
            {paymentType === 'gift_card' && !validatedGiftCard && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gift Card Code *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={giftCardCode}
                    onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleValidateGiftCard();
                      }
                    }}
                    placeholder="GIFT-XXXX-XXXX-XXXX"
                    className={`flex-1 ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-mono touch-manipulation`}
                    style={{ 
                      fontSize: '16px', 
                      minHeight: isMobile ? '44px' : '48px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    disabled={validatingGiftCard}
                  />
                  <button
                    type="button"
                    onClick={handleValidateGiftCard}
                    disabled={validatingGiftCard || !giftCardCode.trim()}
                    className={`${isMobile ? 'px-4 py-2.5 text-[13px]' : 'px-6 py-3 text-base'} font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                      (!validatingGiftCard && giftCardCode.trim())
                        ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' 
                        : 'bg-gray-300 cursor-not-allowed'
                    } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {validatingGiftCard ? (
                      <div className="flex items-center">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Validating...
                      </div>
                    ) : (
                      'Validate'
                    )}
                  </button>
                </div>
                {giftCardError && (
                  <div className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {giftCardError}
                  </div>
                )}
              </div>
            )}

            {/* Gift Card Info Display and Redemption Amount - Only after validation */}
            {paymentType === 'gift_card' && validatedGiftCard && giftCardBalance !== null && (
              <>
                {/* Gift Card Info Display */}
                <div className="mb-4">
                  <div className="bg-white border border-gray-300 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Gift Card Code:</span>
                      <span className="text-sm font-mono font-semibold text-gray-900">{validatedGiftCard.code}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Available Balance:</span>
                      <span className="text-lg font-bold text-gray-900">${giftCardBalance.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Redemption Amount Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Redeem *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm font-medium">$</span>
                    </div>
                    <input
                      type="number"
                      value={giftCardRedemptionAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = parseFloat(value);
                        const maxAmount = Math.min(giftCardBalance || 0, taxCalculation.total);
                        
                        if (value === '' || (numValue >= 0 && numValue <= maxAmount)) {
                          setGiftCardRedemptionAmount(value);
                        }
                      }}
                      min="0"
                      step="0.01"
                      max={Math.min(giftCardBalance || 0, taxCalculation.total)}
                      className={`w-full pl-8 pr-4 ${isMobile ? 'py-2.5' : 'py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                      placeholder="0.00"
                      style={{ 
                        fontSize: '16px', 
                        minHeight: isMobile ? '44px' : '48px',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                    />
                  </div>
                  <div className="mt-2">
                    <p className="text-xs text-gray-600">
                      Maximum: ${Math.min(giftCardBalance || 0, taxCalculation.total).toFixed(2)} 
                      {' '}(based on gift card balance and total amount)
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Stripe Card Entry - Only for stripe payments */}
            {paymentType === 'stripe' && (
              <div className="mb-4">
                <StripeCardEntry
                  amount={taxCalculation.total}
                  businessId={businessId}
                  customerId={selectedCustomer?.id}
                  saveCardOnFile={saveNewCardOnFile}
                  onSaveCardOnFileChange={setSaveNewCardOnFile}
                  onSuccess={async (paymentId) => {
                    console.log('✅ Payment successful:', paymentId);
                    // Create product sale with payment
                    try {
                      // The Stripe charge already created its own completed
                      // payments row — create the sale unpaid and link that
                      // payment to the new invoice below (a DB trigger
                      // allocates it and marks the invoice/sale paid).
                      const sale = await runSale(null, () => ({
                        business_id: businessId,
                        customer_id: selectedCustomer?.id,
                        subtotal_amount: taxCalculation.subtotal,
                        tax_rate: taxCalculation.tax_rate,
                        tax_amount: taxCalculation.tax_amount,
                        total_amount: taxCalculation.total,
                        discount_amount: discountAmount > 0 ? discountAmount : undefined,
                        discount_details: appliedDiscount ? [{
                          id: appliedDiscount.id || 'custom',
                          title: appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : (appliedDiscount.description || 'Custom Discount'),
                          type: appliedDiscount.type,
                          amount: appliedDiscount.value
                        }] : undefined,
                        original_subtotal: discountAmount > 0 ? subtotal + discountAmount : undefined,
                        payment_status: 'unpaid',
                        expected_total_cents: Math.round(total * 100),
                        notes: `Stripe payment - ${paymentId}`,
                        items: cart.map((item, index) => {
                          const taxItem = taxCalculation.items[index];
                          return {
                            stock_item_id: item.product.id,
                            product_name: item.product.name,
                            quantity: item.quantity,
                            unit_price: item.product.price,
                            tax_exempt: taxItem.tax_exempt,
                            tax_rate: taxItem.tax_rate,
                            tax_amount: taxItem.tax_amount,
                            total_price: taxItem.total_price
                          };
                        })
                      }));

                      // Link the completed Stripe payment to the sale and its
                      // invoice; the settle trigger allocates it and marks
                      // both the invoice and the sale paid.
                      const { error: linkError } = await supabase
                        .from('payments')
                        .update({ product_sale_id: sale.id, invoice_id: sale.invoice_id ?? null })
                        .eq('id', paymentId);
                      if (linkError) {
                        console.error('Failed to link Stripe payment to sale/invoice:', linkError);
                      }

                      setCompletedSale({
                        saleNumber: sale.sale_number,
                        productSaleId: sale.id,
                        saleData: {
                          subtotal: taxCalculation.subtotal,
                          taxAmount: taxCalculation.tax_amount,
                          total: taxCalculation.total,
                          discountAmount: discountAmount > 0 ? discountAmount : undefined,
                          discountDetails: appliedDiscount ? {
                            source: appliedDiscount.source,
                            code: appliedDiscount.code,
                            description: appliedDiscount.description,
                            type: appliedDiscount.type,
                            value: appliedDiscount.value
                          } : undefined,
                          items: cart.map(item => ({
                            name: item.product.name,
                            quantity: item.quantity,
                            price: item.product.price
                          }))
                        }
                      });

                      setShowSuccessModal(true);
                    } catch (err: any) {
                      console.error('Error creating product sale:', err);
                      setError(err.message || 'Failed to create product sale');
                    }
                  }}
                  onError={(errorMessage) => {
                    console.error('❌ Payment error:', errorMessage);
                    setError(errorMessage);
                  }}
                  onProcessingChange={(isProcessing) => {
                    setLoading(isProcessing);
                  }}
                />
              </div>
            )}
            
            {/* Pay Button - Only show when appropriate */}
            {paymentType === 'gift_card' && validatedGiftCard && giftCardBalance !== null ? (
              <button
                type="button"
                onClick={handleGiftCardRedemption}
                disabled={loading || !giftCardRedemptionAmount || parseFloat(giftCardRedemptionAmount) <= 0}
                className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-6 py-3.5 text-base'} font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                  (!loading && giftCardRedemptionAmount && parseFloat(giftCardRedemptionAmount) > 0)
                    ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' 
                    : 'bg-gray-300 cursor-not-allowed'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                    Processing...
                  </div>
                ) : (
                  `Redeem Gift Card - $${giftCardRedemptionAmount || '0.00'}`
                )}
              </button>
            ) : paymentType === 'gift_card' && !validatedGiftCard ? (
              // No button shown when gift card is selected but not validated
              null
            ) : paymentType === 'stripe' ? (
              // Stripe payment button is handled inside StripeCardEntry component
              null
            ) : paymentType === 'manual' ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e as any);
                }}
                disabled={loading || cart.length === 0}
                className="w-full px-4 py-3.5 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                    Processing...
                  </div>
                ) : (
                  `Record $${taxCalculation.total.toFixed(2)}`
                )}
              </button>
            ) : null}
          </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {/* Success Modal */}
      {completedSale && (
        <ProductSaleSuccessModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
          saleNumber={completedSale.saleNumber}
          productSaleId={completedSale.productSaleId}
          saleData={completedSale.saleData}
          customer={selectedCustomer}
          paymentMethod={selectedMethod.name}
        />
      )}
    </>
  );
}
